/**
 * Единый каталог видов уведомлений сотрудника (план 155 §2.9, Ф3).
 *
 * ЗАЧЕМ ОДИН СПИСОК. Вид уведомления читают четыре места: колокольчик
 * админки, push-крон, доставка в Telegram и экран настроек «Мой профиль →
 * Telegram». Пока у каждого был свой `switch`, вид с опечаткой молча уходил в
 * «Новое уведомление»: так назначенная задача месяц приходила пушем без текста
 * (API пишет `task_assigned`, крон ждал `task.assigned`, план 152).
 *
 * ЧТО ЗДЕСЬ, А ЧТО НЕТ. Здесь — переход, текст пуша, строка Telegram, подпись в
 * настройках и умолчание. Заголовок и подзаголовок колокольчика остаются в
 * админке (`notification-display.ts`): там разметка экрана и ФИО клиента,
 * которые в Telegram уходить не должны.
 *
 * ПЕРСОНАЛЬНЫЕ ДАННЫЕ. Строка Telegram — функция от ЯВНО перечисленных полей
 * payload. ФИО, телефон и номер карточки клиента, текст клиента, название
 * задачи (в нём обычно фамилия) и текст робота туда не попадают никогда:
 * серверы Telegram за рубежом, это была бы трансграничная передача ПДн
 * (152-ФЗ). Пуш — прежнее поведение плана 110, его тексты не менялись.
 *
 * КЛЮЧИ PAYLOAD. Команды API пишут camelCase (`clientName`, `taskId`), сырой
 * SQL и воркер — snake_case (`client_name`). Читаем оба.
 */

import {
  MESSENGER_BROADCAST_UNDELIVERED,
  broadcastFailureReasonsText,
} from './broadcast-delivery-alert.js';
import {
  CASH_SHEET_CLOSE_BLOCKED,
  CASH_SHEET_CLOSE_EXCEPTION,
  CASH_SHEET_CLOSE_PERMITTED,
  CASH_SHEET_SOURCE_BROKEN,
  cashSheetMoneyLabel,
  cashSheetRub,
  cashSheetSignedRub,
} from './cash-sheet-close.js';
import { CASH_SHIFT_AUTOCLOSE_NEGATIVE } from './cash-shift.js';
import { REFUND_APPROVAL_REQUESTED, REFUND_APPROVED, REFUND_REJECTED } from './refund.js';

export type NotificationPayload = Record<string, unknown>;

export interface EmployeeNotificationKind {
  kind: string;
  /** Подпись вида на экране настроек и в сводке Telegram. */
  label: string;
  /**
   * Вид показывается в настройках. Служебные (подтверждение привязки,
   * вид без писателя) — нет, и в Telegram они не уходят.
   */
  configurable: boolean;
  /** Дублировать в Telegram, пока сотрудник не менял настройку. */
  telegramDefault: boolean;
  /** Явный маршрут перехода в CRM; null — карточка клиента или лента. */
  href: (p: NotificationPayload) => string | null;
  /** Заголовок и текст web-push (короткие: читают с заблокированного экрана). */
  push: (p: NotificationPayload) => { title: string; body: string };
  /** Строка Telegram. ТОЛЬКО белый список полей — см. шапку файла. */
  telegram: (p: NotificationPayload) => string;
}

/** Строковое поле payload: первое непустое из вариантов ключа. */
export function payloadField(p: NotificationPayload, ...keys: string[]): string {
  for (const k of keys) {
    const v = p[k];
    if (typeof v === 'string' && v !== '') return v;
    if (typeof v === 'number') return String(v);
  }
  return '';
}

function joined(...parts: string[]): string {
  return parts.filter(Boolean).join(' · ');
}

/** `2026-09-30` → `30.09`; иначе пусто. */
function dayMonth(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}` : '';
}

function taskHref(p: NotificationPayload): string {
  const taskId = payloadField(p, 'taskId', 'task_id');
  return taskId ? `/tasks?task=${taskId}` : '/tasks';
}

/** Смена сверки: `/cash?shift=<id>` (касса открывает её из URL). */
function shiftHref(p: NotificationPayload): string {
  const shiftId = payloadField(p, 'shift_id', 'shiftId');
  return shiftId ? `/cash?shift=${shiftId}` : '/cash';
}

/** «Кольский 29.09». */
function sheetWhere(p: NotificationPayload): string {
  const branch = payloadField(p, 'branch_name', 'branchName') || 'Смена';
  const day = dayMonth(payloadField(p, 'business_date', 'businessDate'));
  return day ? `${branch} ${day}` : branch;
}

/**
 * « (2 группы, Тек.месяц + Предоплаты нал −1 000 ₽)» — только числа: сколько
 * групп разошлось и разница блокирующих строк денег (правило «сумма»).
 */
function sheetDiffTail(p: NotificationPayload): string {
  const parts: string[] = [];
  const groups = Number(p['group_diff_count'] ?? 0);
  if (Number.isFinite(groups) && groups > 0) parts.push(`${groups} ${pluralGroups(groups)}`);
  const money = Array.isArray(p['money_diffs']) ? (p['money_diffs'] as unknown[]) : [];
  for (const m of money.slice(0, 2)) {
    if (!m || typeof m !== 'object') continue;
    const r = m as Record<string, unknown>;
    const diff = Number(r['diff_kopecks']);
    if (!Number.isFinite(diff) || diff === 0) continue;
    const side = r['side'] === 'cashless' ? 'безнал' : 'нал';
    parts.push(
      `${cashSheetMoneyLabel(String(r['key'] ?? ''))} ${side} ${cashSheetSignedRub(diff)}`,
    );
  }
  return parts.length ? ` (${parts.join(', ')})` : '';
}

/** «в ящике по расчёту минус 1 200 ₽ — пересчитайте кассу». */
function negativeCashText(p: NotificationPayload): string {
  const kopecks = Number(p['expected_kopecks'] ?? p['expectedKopecks']);
  const amount = Number.isFinite(kopecks) ? ` ${cashSheetRub(Math.abs(kopecks))}` : '';
  return `в ящике по расчёту минус${amount} — пересчитайте кассу`;
}

/**
 * «не доставлено 395 из 1043 — Личка не скачала файл 391, другие причины 4 ·
 * ещё в пути 12». Только числа и группы причин (план 163 Ф3): ни текста
 * рассылки, ни имён получателей.
 */
function broadcastUndeliveredText(p: NotificationPayload): string {
  const failed = Number(p['failed'] ?? 0);
  const recipients = Number(p['recipients'] ?? 0);
  const pending = Number(p['pending'] ?? 0);
  const reasons = broadcastFailureReasonsText(p['reasons']);
  let text = `не доставлено ${failed} из ${recipients}`;
  if (reasons) text += ` — ${reasons}`;
  if (Number.isFinite(pending) && pending > 0) text += ` · ещё в пути ${pending}`;
  return text;
}

/** «Рассылка новости 17.10 20:04». */
function broadcastWhat(p: NotificationPayload): string {
  const head = payloadField(p, 'news_send_id', 'newsSendId') ? 'Рассылка новости' : 'Рассылка';
  const when = payloadField(p, 'sent_label', 'sentLabel');
  return when ? `${head} ${when}` : head;
}

/** «8 360 ₽» из `amount_kopecks`; без суммы — пусто. */
function refundAmount(p: NotificationPayload): string {
  const kopecks = Number(p['amount_kopecks'] ?? p['amountKopecks']);
  return Number.isFinite(kopecks) && kopecks > 0 ? cashSheetRub(kopecks) : '';
}

function refundToDeposit(p: NotificationPayload): boolean {
  return payloadField(p, 'method') === 'deposit';
}

/** «Возврат 8 360 ₽» — сумма, если она есть в payload. */
function refundHead(p: NotificationPayload): string {
  const amount = refundAmount(p);
  return amount ? `Возврат ${amount}` : 'Возврат';
}

/** Что делать после подтверждения: выдать деньги или ничего (депозит). */
function refundApprovedAction(p: NotificationPayload): string {
  if (refundToDeposit(p)) return 'зачислено на депозит';
  const amount = refundAmount(p);
  return amount ? `выдайте ${amount}` : 'выдайте деньги';
}

function pluralGroups(n: number): string {
  const abs = n % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return 'групп';
  if (last === 1) return 'группа';
  if (last >= 2 && last <= 4) return 'группы';
  return 'групп';
}

export const EMPLOYEE_NOTIFICATION_KINDS: readonly EmployeeNotificationKind[] = [
  {
    kind: 'task_assigned',
    label: 'Назначена задача',
    configurable: true,
    telegramDefault: true,
    href: taskHref,
    push: (p) => ({ title: 'Новая задача', body: payloadField(p, 'title') }),
    telegram: (p) => {
      const due = dayMonth(payloadField(p, 'dueDate', 'due_date'));
      return due ? `Новая задача, срок ${due}` : 'Новая задача';
    },
  },
  {
    kind: 'client_task_reminder',
    label: 'Напоминание по задаче',
    configurable: true,
    telegramDefault: true,
    href: taskHref,
    push: (p) => ({ title: 'Напоминание по задаче', body: payloadField(p, 'title') }),
    telegram: () => 'Напоминание по задаче',
  },
  {
    kind: 'correction_request',
    label: 'Заявка на исправление оплаты',
    configurable: true,
    telegramDefault: true,
    href: taskHref,
    // План 150 Ф6: сотрудник сообщил об ошибке оплаты (решение владельца 8).
    push: (p) => ({
      title: 'Заявка на исправление',
      body: joined(payloadField(p, 'title'), payloadField(p, 'client_name', 'clientName')),
    }),
    telegram: () => 'Заявка на исправление оплаты',
  },
  {
    kind: 'assistant_correction',
    label: 'Правка через ассистента',
    configurable: true,
    telegramDefault: true,
    href: () => '/cash/corrections',
    // План 150 Ф5: владелец узнаёт о КАЖДОЙ правке через ассистента.
    push: (p) => ({
      title: 'Правка через ассистента',
      body: joined(payloadField(p, 'headline'), payloadField(p, 'client_name', 'clientName')),
    }),
    telegram: () => 'Правка через ассистента',
  },
  // --- Сверка смены с таблицей учёта (план 155 Ф2, §2.6) ------------------
  // payload пишут API и воркер snake_case: shift_id, branch_name,
  // business_date, числа. Клиентов в нём нет; в Telegram идут только филиал,
  // дата, число групп и разница денег по блокирующим строкам.
  {
    kind: CASH_SHEET_CLOSE_BLOCKED,
    label: 'Смена не закрывается — расхождение сверки',
    configurable: true,
    telegramDefault: true,
    href: shiftHref,
    push: (p) => ({
      title: 'Смена не закрывается',
      body: `${sheetWhere(p)}: расхождение со сверкой${sheetDiffTail(p)}`,
    }),
    telegram: (p) =>
      `${sheetWhere(p)}: смена не закрывается — расхождение сверки${sheetDiffTail(p)}`,
  },
  {
    kind: CASH_SHEET_CLOSE_PERMITTED,
    label: 'Закрытие смены разрешено',
    configurable: true,
    telegramDefault: true,
    href: shiftHref,
    push: (p) => ({
      title: 'Закрытие смены разрешено',
      body: joined(sheetWhere(p), payloadField(p, 'permitted_by_name')),
    }),
    telegram: (p) => `${sheetWhere(p)}: закрытие смены разрешено`,
  },
  {
    kind: CASH_SHEET_CLOSE_EXCEPTION,
    label: 'Смена закрыта с расхождением или без сверки',
    configurable: true,
    telegramDefault: true,
    href: shiftHref,
    push: (p) => ({
      title:
        payloadField(p, 'status') === 'unchecked'
          ? 'Смена закрыта без сверки'
          : 'Смена закрыта с расхождением',
      body: `${sheetWhere(p)}${sheetDiffTail(p)}`,
    }),
    telegram: (p) =>
      payloadField(p, 'status') === 'unchecked'
        ? `${sheetWhere(p)}: смена закрыта без сверки, задача управляющему`
        : `${sheetWhere(p)}: смена закрыта с расхождением, задача управляющему${sheetDiffTail(p)}`,
  },
  {
    kind: CASH_SHEET_SOURCE_BROKEN,
    label: 'Сверка с таблицей не работает',
    configurable: true,
    telegramDefault: true,
    href: () => '/reference?tab=sheetSources',
    push: (p) => ({
      title: 'Сверка с таблицей не работает',
      body: joined(payloadField(p, 'branch_name'), payloadField(p, 'message')),
    }),
    // Пояснение пишет CRM (`unavailableMessage`), а не Google: адрес
    // сервисного аккаунта и подсказка, ответа Google там нет.
    telegram: (p) => {
      const branch = payloadField(p, 'branch_name') || 'Филиал';
      const why = payloadField(p, 'message');
      return `${branch}: сверка с таблицей не работает${why ? ` — ${why}` : ''}`;
    },
  },
  // План 163 Ф3: недоставленных людей по рассылке не меньше порога. payload
  // воркера: alert_key, broadcast_ids, news_send_id, sent_label, failed,
  // recipients, pending, reasons [{group, count}], preview. `preview` (начало
  // текста рассылки) — только для колокольчика: в Telegram и пуш не идёт.
  {
    kind: MESSENGER_BROADCAST_UNDELIVERED,
    label: 'Рассылка дошла не всем',
    configurable: true,
    telegramDefault: true,
    href: () => '/app-content?tab=broadcast',
    push: (p) => ({ title: 'Рассылка дошла не всем', body: broadcastUndeliveredText(p) }),
    telegram: (p) => `${broadcastWhat(p)} дошла не всем: ${broadcastUndeliveredText(p)}`,
  },
  // План 154 Ф3 (ревью S2): робот в 03:59 не закрыл смену — по расчёту в
  // ящике минус. payload воркера: shift_id, branch_name, business_date,
  // expected_kopecks (отрицательное). Клиентов нет — только филиал, дата, сумма.
  {
    kind: CASH_SHIFT_AUTOCLOSE_NEGATIVE,
    label: 'Смена не закрылась автоматически',
    configurable: true,
    telegramDefault: true,
    href: shiftHref,
    push: (p) => ({
      title: 'Смену не закрыть автоматически',
      body: `${sheetWhere(p)}: ${negativeCashText(p)}`,
    }),
    telegram: (p) => `${sheetWhere(p)}: смену не закрыть автоматически — ${negativeCashText(p)}`,
  },
  // --- Очередь возвратов сверх лимита (план 168 §2.5, В4) -----------------
  // Push и колокольчик называют клиента. Telegram — только сумма, способ и
  // филиал: ни ФИО, ни номера карточки, ни причины отказа (её пишет человек,
  // и в ней бывает фамилия клиента).
  {
    kind: REFUND_APPROVAL_REQUESTED,
    label: 'Возврат ждёт подтверждения',
    configurable: true,
    // Заявок сверх лимита — единицы в месяц, и без ответа деньги не выдают.
    telegramDefault: true,
    href: (p) => {
      const refundId = payloadField(p, 'refund_id', 'refundId');
      return refundId ? `/cash/refunds?refund=${refundId}` : '/cash/refunds';
    },
    push: (p) => ({
      title: 'Возврат ждёт подтверждения',
      body: joined(
        refundAmount(p),
        payloadField(p, 'client_name', 'clientName'),
        payloadField(p, 'branch_name', 'branchName'),
      ),
    }),
    telegram: (p) =>
      joined(`${refundHead(p)} ждёт подтверждения`, payloadField(p, 'branch_name', 'branchName')),
  },
  {
    kind: REFUND_APPROVED,
    label: 'Возврат подтверждён',
    configurable: true,
    telegramDefault: true,
    // Переход — карточка клиента: там возврат, и видно, кому выдавать.
    href: () => null,
    push: (p) => ({
      title: 'Возврат подтверждён',
      body: joined(
        refundToDeposit(p)
          ? [refundAmount(p), 'зачислено на депозит'].filter(Boolean).join(' ')
          : `Выдайте ${refundAmount(p) || 'деньги'}`,
        payloadField(p, 'client_name', 'clientName'),
      ),
    }),
    telegram: (p) =>
      joined(
        refundToDeposit(p)
          ? `${refundHead(p)} подтверждён — ${refundApprovedAction(p)}`
          : `Возврат подтверждён — ${refundApprovedAction(p)}`,
        payloadField(p, 'branch_name', 'branchName'),
      ),
  },
  {
    kind: REFUND_REJECTED,
    label: 'Возврат отклонён',
    configurable: true,
    telegramDefault: true,
    href: () => null,
    push: (p) => ({
      title: 'Возврат отклонён',
      body: joined(
        refundAmount(p),
        payloadField(p, 'client_name', 'clientName'),
        payloadField(p, 'rejection_reason', 'rejectionReason'),
      ),
    }),
    telegram: (p) =>
      joined(`${refundHead(p)} отклонён`, payloadField(p, 'branch_name', 'branchName')),
  },
  {
    kind: 'automation',
    label: 'Уведомления автоматизации',
    configurable: true,
    telegramDefault: true,
    href: (p) => {
      const dealId = payloadField(p, 'dealId', 'deal_id');
      return dealId ? `/deals?deal=${dealId}` : null;
    },
    push: (p) => {
      const rule = payloadField(p, 'rule_name', 'ruleName');
      return {
        title: rule ? `Автоматизация: ${rule}` : 'Автоматизация',
        body: payloadField(p, 'text'),
      };
    },
    // Имя правила задаёт школа; текст робота — нет: в нём бывает ФИО клиента.
    telegram: (p) => {
      const rule = payloadField(p, 'rule_name', 'ruleName');
      return rule ? `Автоматизация: ${rule}` : 'Автоматизация';
    },
  },
  {
    kind: 'shift_tasks_digest',
    label: 'Задачи на смену',
    configurable: true,
    telegramDefault: false,
    href: () => '/tasks',
    push: (p) => ({
      title: 'Задачи на смену',
      body: `Задач на сегодня: ${payloadField(p, 'count') || '0'}`,
    }),
    telegram: (p) => `Задач на смену: ${payloadField(p, 'count') || '0'}`,
  },
  {
    kind: 'miniapp.link_request',
    label: 'Заявка на привязку мессенджера',
    configurable: true,
    // ≈1 в день каждому из 18 адресатов (прод, сентябрь) — по умолчанию выкл.
    telegramDefault: false,
    href: () => '/notifications?tab=links',
    push: (p) => ({
      title: 'Заявка на привязку',
      body: joined(
        payloadField(p, 'requester_name', 'requesterName'),
        payloadField(p, 'entered_phone', 'enteredPhone'),
      ),
    }),
    telegram: () => 'Заявка на привязку мессенджера',
  },
  {
    kind: 'client.phone_change_request',
    label: 'Заявка на смену номера',
    configurable: true,
    telegramDefault: false,
    href: () => '/notifications?tab=phone-change',
    push: (p) => ({
      title: 'Заявка на смену номера',
      body: payloadField(p, 'requested_phone', 'requestedPhone'),
    }),
    telegram: () => 'Заявка на смену номера',
  },
  {
    kind: 'enrollment',
    label: 'Новая запись на занятие',
    configurable: true,
    // ≈10 в день на человека — заглушила бы задачи.
    telegramDefault: false,
    href: () => null,
    push: (p) => ({
      title: 'Запись на занятие',
      body: payloadField(p, 'client_name', 'clientName'),
    }),
    telegram: (p) => {
      const group = payloadField(p, 'groupName', 'group_name');
      const waitlist = p['isWaitlist'] === true || p['is_waitlist'] === true;
      const head = waitlist ? 'Запись в лист ожидания' : 'Новая запись';
      return group ? `${head}: ${group}` : head;
    },
  },
  {
    kind: 'enrollment_cancelled',
    label: 'Отмена записи',
    configurable: true,
    telegramDefault: false,
    href: () => null,
    push: (p) => ({
      title: 'Запись отменена',
      body: joined(
        payloadField(p, 'client_name', 'clientName'),
        payloadField(p, 'group_name', 'groupName'),
      ),
    }),
    telegram: (p) => {
      const group = payloadField(p, 'groupName', 'group_name');
      return group ? `Запись отменена: ${group}` : 'Запись отменена';
    },
  },
  {
    // Подтверждение привязки (план 155 §2.9 п. 3): колокольчик сотрудника
    // узнаёт, что к учётке привязали Telegram, — на случай, если ссылку
    // переслали чужому. В сам Telegram не дублируется: смысла нет.
    kind: 'staff_telegram.linked',
    label: 'Telegram привязан',
    configurable: false,
    telegramDefault: false,
    href: () => null,
    push: (p) => ({
      title: 'Telegram привязан',
      body: payloadField(p, 'username') ? `@${payloadField(p, 'username')}` : '',
    }),
    telegram: () => 'Telegram привязан к вашей учётке CRM',
  },
  {
    // Писателя этого вида сейчас нет (сверка 29.09): уведомлений о входящих
    // сообщениях никто не создаёт. Держим ради пуша — исходной цели плана 110.
    kind: 'messenger.inbound',
    label: 'Новое сообщение',
    configurable: false,
    telegramDefault: false,
    href: () => '/messenger',
    push: (p) => ({
      title: 'Новое сообщение',
      body: payloadField(p, 'client_name', 'clientName') || 'Клиент написал в чат',
    }),
    telegram: () => 'Новое сообщение в чате',
  },
];

const BY_KIND = new Map(EMPLOYEE_NOTIFICATION_KINDS.map((k) => [k.kind, k]));

export function employeeNotificationKind(kind: string): EmployeeNotificationKind | undefined {
  return BY_KIND.get(kind);
}

/** Виды, которые сотрудник видит в настройках «Мой профиль → Telegram». */
export const CONFIGURABLE_NOTIFICATION_KINDS = EMPLOYEE_NOTIFICATION_KINDS.filter(
  (k) => k.configurable,
);

/**
 * Куда ведёт уведомление: явный маршрут вида, иначе карточка клиента, иначе
 * лента уведомлений. Совпадает с переходом колокольчика.
 */
export function notificationTarget(kind: string, p: NotificationPayload): string {
  const explicit = employeeNotificationKind(kind)?.href(p) ?? null;
  if (explicit) return explicit;
  const clientId = payloadField(p, 'clientId', 'client_id');
  return clientId ? `/clients?client=${clientId}` : '/notifications';
}

/** Пуш по виду; неизвестный вид не молчит, а показывает нейтральный текст. */
export function notificationPush(
  kind: string,
  p: NotificationPayload,
): { title: string; body: string } {
  const def = employeeNotificationKind(kind);
  return def ? def.push(p) : { title: 'Dance School CRM', body: 'Новое уведомление' };
}

/** Строка Telegram по виду; неизвестный вид — нейтральная фраза без полей. */
export function notificationTelegramLine(kind: string, p: NotificationPayload): string {
  const def = employeeNotificationKind(kind);
  return def ? def.telegram(p) : 'Новое уведомление в CRM';
}

/**
 * Включён ли вид для Telegram у сотрудника. `prefs` — его отступления от
 * умолчания (`employee_notification_prefs`). Служебные виды не уходят никогда;
 * неизвестный вид (писатель новее каталога) — по умолчанию уходит.
 */
export function isTelegramKindEnabled(kind: string, prefs: ReadonlyMap<string, boolean>): boolean {
  const def = employeeNotificationKind(kind);
  if (def && !def.configurable) return false;
  const own = prefs.get(kind);
  if (own !== undefined) return own;
  return def ? def.telegramDefault : true;
}
